import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Video, VideoOff, Mic, MicOff, PhoneOff, Send, Loader2, Star, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import BirthChart3D from "@/components/astrology/BirthChart3D";
import { DEFAULT_ICE, loadPublicIce } from "@/lib/iceServers";


/**
 * /astrology/room/:booking_id — post-payment ephemeral session room.
 *   chat: text-only (polls messages)
 *   video: WebRTC with signaling piggy-backed on the same message channel
 *          via /astrology/rooms/{id}/rtc (kind = offer/answer/ice/hangup)
 */
export default function AstrologyRoom() {
  const { booking_id } = useParams();
  const { user } = useAuth();
  const [room, setRoom]   = useState(null);
  const [msgs, setMsgs]   = useState([]);
  const [text, setText]   = useState("");
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [ended, setEnded] = useState(false);
  const sinceRef = useRef(null);

  // WebRTC state
  const [videoOn, setVideoOn]     = useState(false);
  const [micOn, setMicOn]         = useState(true);
  const [rtcState, setRtcState]   = useState("idle");   // idle|connecting|live|failed
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const localVideoRef  = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef          = useRef(null);
  const localStreamRef = useRef(null);
  const seenSigRef     = useRef(new Set());
  const mediaRecRef    = useRef(null);
  const chunksRef      = useRef([]);

  // ── Load initial room ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get(`/astrology/rooms/${booking_id}`);
        setRoom(r.data);
        setMsgs(r.data.messages || []);
        setRemaining(r.data.remaining_sec || 0);
        sinceRef.current = r.data.now;
      } catch (e) { setError(e?.response?.data?.detail || "Couldn't open room"); }
    })();
  }, [booking_id, user]);

  // ── Countdown timer ───────────────────────────────────────────────────
  useEffect(() => {
    if (!room || ended) return;
    const t = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [room, ended]);

  // ── Poll new messages (chat + RTC signals) ────────────────────────────
  useEffect(() => {
    if (!room || ended) return;
    const poll = async () => {
      try {
        const r = await api.get(`/astrology/rooms/${booking_id}/messages`, { params: { since: sinceRef.current } });
        sinceRef.current = r.data.now;
        (r.data.messages || []).forEach(m => {
          if (m.kind?.startsWith("rtc_")) handleRtcSignal(m);
          else setMsgs(prev => prev.some(x => x.msg_id === m.msg_id) ? prev : [...prev, m]);
        });
      } catch { /* silent */ }
    };
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, ended]);

  // ── WebRTC helpers ────────────────────────────────────────────────────
  const iceRef = useRef(DEFAULT_ICE);
  useEffect(() => { loadPublicIce().then((l) => { iceRef.current = l; }); }, []);
  const ensurePc = () => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection({ iceServers: iceRef.current });
    pc.onicecandidate = e => {
      if (e.candidate) api.post(`/astrology/rooms/${booking_id}/rtc`, { kind: "ice", payload: e.candidate.toJSON() });
    };
    pc.ontrack = e => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = e.streams[0];
        setRtcState("live");
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") setRtcState("failed");
      if (pc.connectionState === "disconnected") setRtcState("idle");
    };
    pcRef.current = pc;
    return pc;
  };

  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: micOn });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      const pc = ensurePc();
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      setVideoOn(true);
      setRtcState("connecting");
      // Create + send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await api.post(`/astrology/rooms/${booking_id}/rtc`, { kind: "offer", payload: { sdp: offer.sdp, type: offer.type } });
      // Auto-start recording of the local mixed stream — user can toggle off.
      startRecording(stream);
      toast.success("Camera on · recording started · waiting for astrologer");
    } catch (e) {
      toast.error("Camera access denied");
      setRtcState("failed");
    }
  };

  const startRecording = (stream) => {
    try {
      if (typeof MediaRecorder === "undefined") return;
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus"
                 : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus"
                 : "video/webm";
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 900_000 });
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = uploadRecording;
      mr.start(2000);  // flush every 2s
      mediaRecRef.current = mr;
      setRecording(true);
    } catch (e) {
      console.warn("recording start failed", e);
    }
  };

  const uploadRecording = async () => {
    if (!chunksRef.current.length) { setRecording(false); return; }
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0].type || "video/webm" });
    chunksRef.current = [];
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", blob, `astro-${booking_id}.webm`);
      const r = await api.post(`/astrology/rooms/${booking_id}/recording`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Recording saved (${Math.round((r.data.bytes || 0) / 1024)} KB)`);
      loadRecordings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recording upload failed");
    } finally {
      setRecording(false); setUploading(false);
    }
  };

  const loadRecordings = async () => {
    try {
      const r = await api.get(`/astrology/rooms/${booking_id}/recordings`);
      setRecordings(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { if (room) loadRecordings(); /* eslint-disable-next-line */ }, [room]);

  const saveToDrive = async (recId) => {
    try {
      const r = await api.get(`/astrology/recordings/${recId}/drive-link`);
      // Prefer opening a share URL in a new tab; falls back to direct download.
      if (r.data.google_drive_import_url) {
        window.open(r.data.google_drive_import_url, "_blank", "noopener,noreferrer");
      }
      if (r.data.source_url) {
        // Also trigger a direct save so users on desktop always have a copy
        const a = document.createElement("a");
        a.href = r.data.source_url;
        a.download = r.data.filename || `astro-recording-${recId}.webm`;
        a.rel = "noopener noreferrer";
        a.click();
      }
    } catch (e) { toast.error("Save to Drive link failed"); }
  };

  const stopVideo = () => {
    // Stop recorder first — its onstop handler uploads the blob
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      try { mediaRecRef.current.stop(); } catch { /* ignore */ }
    }
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    setVideoOn(false);
    setRtcState("idle");
    api.post(`/astrology/rooms/${booking_id}/rtc`, { kind: "hangup", payload: {} }).catch(() => {});
  };

  const toggleMic = () => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getAudioTracks().forEach(t => t.enabled = !micOn);
    setMicOn(m => !m);
  };

  const handleRtcSignal = async (m) => {
    if (seenSigRef.current.has(m.msg_id)) return;
    seenSigRef.current.add(m.msg_id);
    if (m.sender_id === user.user_id) return;    // ignore our own echoes
    const pc = ensurePc();
    try {
      if (m.kind === "rtc_offer") {
        // Answering side needs media too if they haven't started
        if (!localStreamRef.current) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            localStreamRef.current = stream;
            if (localVideoRef.current) localVideoRef.current.srcObject = stream;
            stream.getTracks().forEach(t => pc.addTrack(t, stream));
            setVideoOn(true);
          } catch { toast.error("Camera access needed to answer"); return; }
        }
        await pc.setRemoteDescription({ type: "offer", sdp: m.payload.sdp });
        const ans = await pc.createAnswer();
        await pc.setLocalDescription(ans);
        await api.post(`/astrology/rooms/${booking_id}/rtc`, { kind: "answer", payload: { sdp: ans.sdp, type: ans.type } });
        setRtcState("connecting");
      } else if (m.kind === "rtc_answer") {
        await pc.setRemoteDescription({ type: "answer", sdp: m.payload.sdp });
      } else if (m.kind === "rtc_ice") {
        try { await pc.addIceCandidate(m.payload); } catch { /* mid-negotiation ICE — ignore */ }
      } else if (m.kind === "rtc_hangup") {
        stopVideo();
      }
    } catch (e) { console.warn("RTC error", e); }
  };

  // ── Chat send ─────────────────────────────────────────────────────────
  const send = async () => {
    if (!text.trim()) return;
    const t = text.trim(); setText("");
    try {
      const r = await api.post(`/astrology/rooms/${booking_id}/message`, { text: t });
      setMsgs(m => [...m, r.data]);
    } catch (e) { toast.error("Send failed"); }
  };

  // ── End session + rating ──────────────────────────────────────────────
  const [showEnd, setShowEnd] = useState(false);

  if (error) return (
    <div className="min-h-screen grid place-items-center text-center p-4">
      <div><div className="font-black text-xl text-stone-900">{error}</div></div>
    </div>
  );
  if (!room) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin"/></div>;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="min-h-screen bg-stone-950 text-white flex flex-col" data-testid="astrology-room-page">
      <div className="bg-stone-900 border-b border-stone-800 px-4 py-2 flex items-center gap-3">
        <div className="text-sm font-black">Session · {room.booking?.astrologer_name || "Astrologer"}</div>
        <span className="text-xs bg-amber-400 text-stone-900 rounded-full px-2 py-0.5 font-black font-mono" data-testid="room-timer">{mm}:{ss}</span>
        <span className="text-[10px] text-stone-400 uppercase">{room.channel}</span>
        <div className="ml-auto flex gap-1">
          {room.channel === "video" && !videoOn && (
            <Button size="sm" onClick={startVideo} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black h-8 text-[11px]" data-testid="room-start-video">
              <Video size={12} className="mr-1"/> Start video
            </Button>
          )}
          {videoOn && (
            <>
              <Button size="sm" onClick={toggleMic} variant="outline" className="rounded-full bg-stone-800 border-stone-700 text-white font-black h-8 text-[11px]" data-testid="room-toggle-mic">
                {micOn ? <Mic size={12}/> : <MicOff size={12}/>}
              </Button>
              <Button size="sm" onClick={stopVideo} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-black h-8 text-[11px]" data-testid="room-stop-video">
                <VideoOff size={12}/>
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => setShowEnd(true)} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-black h-8 text-[11px]" data-testid="room-end-btn">
            <PhoneOff size={12} className="mr-1"/> End
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Video pane */}
        {room.channel === "video" && (
          <div className="md:w-1/2 bg-black relative min-h-[300px]" data-testid="room-video-pane">
            <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" data-testid="room-remote-video"/>
            {rtcState !== "live" && (
              <div className="absolute inset-0 grid place-items-center text-white/70 text-sm bg-gradient-to-b from-indigo-950/90 to-stone-950/90" data-testid="room-video-idle">
                <div className="text-center max-w-xs">
                  <BirthChart3D
                    rashi={room.booking?.rashi}
                    lagna={room.booking?.lagna}
                    sun={room.booking?.sun_sign}
                    nakshatra={room.booking?.nakshatra || "Ready"}
                  />
                  <div className="mt-3 text-[11px] text-amber-300 font-black uppercase tracking-widest inline-flex items-center gap-1">
                    {rtcState === "connecting" ? <><Loader2 size={11} className="animate-spin"/> Connecting…</> :
                     rtcState === "failed"     ? "Connection failed — try again" :
                     <>✨ Waiting for astrologer</>}
                  </div>
                </div>
              </div>
            )}
            <video ref={localVideoRef} autoPlay playsInline muted className="absolute bottom-2 right-2 w-32 h-24 rounded-lg border-2 border-white/20 object-cover" data-testid="room-local-video"/>
            {(recording || uploading) && (
              <div className="absolute top-2 left-2 rounded-full bg-black/70 border border-red-500 px-2 py-0.5 text-[10px] text-red-400 font-black inline-flex items-center gap-1" data-testid="room-recording-indicator">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"/>
                {uploading ? "SAVING…" : "REC"}
              </div>
            )}
          </div>
        )}

        {/* Chat pane */}
        <div className="flex-1 flex flex-col bg-stone-900 min-h-[300px]" data-testid="room-chat-pane">
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {msgs.filter(m => !m.kind?.startsWith("rtc_")).map(m => (
              <div key={m.msg_id} className={`flex ${m.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-sm ${m.sender_id === user?.user_id ? "bg-amber-400 text-stone-900" : "bg-stone-700 text-white"}`} data-testid={`room-msg-${m.msg_id}`}>
                  <div className="text-[9px] uppercase font-black opacity-60">{m.role}</div>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-800 p-2 flex gap-2">
            <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Type a message…" className="bg-stone-800 border-stone-700 rounded-full text-white" data-testid="room-input"/>
            <Button onClick={send} disabled={!text.trim()} className="rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black" data-testid="room-send">
              <Send size={14}/>
            </Button>
          </div>
          {recordings.length > 0 && (
            <div className="border-t border-stone-800 p-2 space-y-1" data-testid="room-recordings">
              <div className="text-[10px] uppercase font-black text-amber-300">Recordings ({recordings.length})</div>
              {recordings.map(r => (
                <div key={r.recording_id} className="flex items-center gap-2 text-xs bg-stone-800 rounded-xl px-2 py-1.5" data-testid={`recording-row-${r.recording_id}`}>
                  <span className="text-red-400">●</span>
                  <span className="flex-1 truncate text-white/70">{Math.round((r.bytes || 0) / 1024)} KB · {r.created_at?.split("T")[1]?.split(".")[0]}</span>
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 font-black" data-testid={`recording-open-${r.recording_id}`}>Open</a>
                  <button onClick={() => saveToDrive(r.recording_id)} className="text-emerald-400 hover:text-emerald-300 font-black" data-testid={`recording-drive-${r.recording_id}`}>Drive</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showEnd && <EndRatingModal bookingId={booking_id} onClose={done => { setShowEnd(false); if (done) setEnded(true); }}/>}
    </div>
  );
}

function EndRatingModal({ bookingId, onClose }) {
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");
  const [tip,    setTip]    = useState(0);
  const [busy,   setBusy]   = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/astrology/rooms/${bookingId}/end`, { rating, review: review || null });
      toast.success("Thanks for rating!");
      if (tip > 0) {
        const r = await api.post(`/astrology/rooms/${bookingId}/tip`, { amount_inr: tip, origin_url: window.location.origin });
        if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      }
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-stone-900 border border-white/10 p-5 w-full max-w-sm space-y-3 text-white" data-testid="room-end-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black">How was your session?</h3>
          <button onClick={() => onClose(false)} className="text-white/50 hover:text-white"><X size={16}/></button>
        </div>
        <div className="flex justify-center gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} onClick={() => setRating(n)} className={`p-1 ${n <= rating ? "text-amber-400" : "text-stone-600"}`} data-testid={`room-star-${n}`}>
              <Star size={26} fill={n <= rating ? "currentColor" : "none"}/>
            </button>
          ))}
        </div>
        <textarea value={review} onChange={e => setReview(e.target.value)} rows={3} placeholder="Optional review…" className="w-full bg-stone-800 border border-stone-700 rounded-2xl text-sm p-2 text-white" data-testid="room-review"/>
        <div>
          <div className="text-[10px] uppercase font-black text-white/60 mb-1">Add a tip? (optional)</div>
          <div className="flex gap-1">
            {[0, 50, 100, 200, 500].map(t => (
              <button key={t} onClick={() => setTip(t)} className={`flex-1 rounded-full text-xs font-black py-1.5 ${tip === t ? "bg-amber-400 text-stone-900" : "bg-stone-800 text-white"}`} data-testid={`room-tip-${t}`}>
                {t === 0 ? "No tip" : `₹${t}`}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black h-10" data-testid="room-submit-rating">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : tip > 0 ? `Rate & tip ₹${tip}` : "Submit rating"}
        </Button>
      </div>
    </div>
  );
}
